import { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { MdHelpers } from '../../helpers/mdHelpers';
import { TooltipEditor } from '../organisms/TooltipEditor';
import DatabaseTooltipMethods from '../../data/database.tooltips.methods';
import { inspectionDB } from '../../data/database.structure';
import { TooltipModel } from '../../data/types';

interface TooltipModalProps {
  isOpen: boolean;
  onClose: () => void;
  tooltipKey: string;
  content?: string;
}

export function TooltipModal({
  isOpen,
  onClose,
  tooltipKey: tooltipKeyProp,
  content,
}: TooltipModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const tooltipKey = useMemo(
    () => MdHelpers.normaliseTooltipKeyComponent(tooltipKeyProp),
    [tooltipKeyProp],
  );

  const env = useMemo(() => {
    return import.meta.env.VITE_ENV;
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleAnimationEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const tooltipDbMethods = useMemo(
    () => new DatabaseTooltipMethods(inspectionDB),
    [],
  );
  const [tooltip, setTooltip] = useState<TooltipModel | null>(null);
  const retrieveTooltip = useCallback(
    async () => setTooltip(await tooltipDbMethods.getTooltip(tooltipKey)),
    [tooltipDbMethods, tooltipKey],
  );

  // Prefer the freshly-fetched DB content so edits made in the in-app editor
  // show as soon as it closes (the editor's onClose re-fetches); fall back to
  // the caller-provided content only until that fetch resolves — otherwise the
  // stale cached prop would win permanently.
  const innerHtml = useMemo(
    () => MdHelpers.resolveTooltipInnerHtml(tooltip, content),
    [tooltip, content],
  );

  useEffect(() => {
    retrieveTooltip();
  }, [retrieveTooltip, tooltipDbMethods, tooltipKey]);

  if (!isOpen && !isAnimating) return null;

  if (isEditorOpen)
    return (
      <div className="fixed inset-0 z-50" style={{ marginTop: 69 }}>
        <TooltipEditor
          tooltipKey={tooltipKey}
          isOpen={isEditorOpen}
          onClose={() => {
            setIsEditorOpen(false);
            retrieveTooltip();
          }}
        />
      </div>
    );

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center px-4 py-6 sm:p-0">
      <div
        className={`fixed inset-0 transition-opacity duration-200 ease-in ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        aria-hidden="true"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-inkDisabled opacity-75"></div>
      </div>

      <div
        className={`fixed right-0 bottom-0 left-0 w-full transform overflow-y-scroll rounded-t-lg bg-white shadow-xl transition-transform duration-200 ease-out ${
          isAnimating && isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          maxHeight: '80vh',
          transitionDelay: isOpen ? '200ms' : '0ms',
          transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)', // Custom easing function for a smoother long animation
        }}
        onTransitionEnd={handleAnimationEnd}
      >
        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div className="sm:flex sm:items-start">
            <div className="mt-3 w-full text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="flex items-center justify-between text-lg leading-6 font-normal text-ink">
                {tooltip?.heading}
                <button
                  onClick={onClose}
                  className="text-inkDisabled transition duration-150 ease-in-out hover:text-inkMuted focus:text-inkMuted focus:outline-none"
                >
                  <X size={24} />
                </button>
              </h3>
              <div className="mt-2">
                <div dangerouslySetInnerHTML={{ __html: innerHtml }}></div>
              </div>
            </div>
          </div>
        </div>

        {(env === 'local' || env === 'dv') && (
          <button
            className="float-right px-5 py-3"
            type="button"
            onClick={() => setIsEditorOpen(true)}
          >
            Open in editor
          </button>
        )}

        {env !== 'local' && env !== 'dv' && <div className="py-4"></div>}
      </div>
    </div>
  );
}
